import { useBoolean } from 'minimal-shared/hooks';
import { useRef, useMemo, useEffect, useCallback } from 'react';
import { isActiveLink, isExternalLink } from 'minimal-shared/utils';

import { usePathname } from 'src/routes/hooks';

import { NavItem } from './nav-item';
import { navSectionClasses } from '../styles';
import { NavUl, NavLi, NavCollapse } from '../components';
import { NavItemWithFavorite } from '../components/nav-item-with-favorite';

import type { NavListProps, NavSubListProps, NavItemDataProps } from '../types';

// ----------------------------------------------------------------------

/**
 * ¿Alguna de las opciones de dentro (a cualquier profundidad) es la página en la que estamos?
 *
 * Hace falta porque el desplegable se abre según su propia ruta, y muchos grupos apuntan a la
 * primera de sus opciones ("Prospección" → Agencias). Al entrar en otra opción del grupo
 * ("Big players"), esa ruta ya no casa y el desplegable se cerraba solo. Con esto, un grupo
 * sigue abierto mientras estés en cualquiera de sus secciones.
 */
function hasActiveChild(children: NavItemDataProps[] | undefined, pathname: string): boolean {
  if (!children?.length) return false;

  return children.some(
    (child) =>
      isActiveLink(pathname, child.path, !!child.children) ||
      hasActiveChild(child.children, pathname)
  );
}

export function NavList({
  data,
  depth,
  render,
  slotProps,
  checkPermissions,
  enabledRootRedirect,
  parentPath,
}: NavListProps & { parentPath?: string }) {
  const pathname = usePathname();
  const navItemRef = useRef<HTMLButtonElement>(null);

  // Activo si lo es la propia opción o cualquiera de las que cuelgan de ella.
  const isActive = useMemo(
    () =>
      isActiveLink(pathname, data.path, !!data.children) ||
      hasActiveChild(data.children, pathname),
    [pathname, data.path, data.children]
  );

  const { value: open, onFalse: onClose, onTrue: onOpen, onToggle } = useBoolean(isActive);

  useEffect(() => {
    // Al navegar: el grupo de la página en la que estamos se abre, y el resto se cierran.
    if (isActive) {
      onOpen();
    } else {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, isActive]);

  const handleToggleMenu = useCallback(() => {
    if (data.children) {
      onToggle();
    }
  }, [data.children, onToggle]);

  const renderNavItem = () => (
    <NavItemWithFavorite
      item={data}
      parentPath={parentPath}
      showFavoriteButton={depth === 1 || (depth === 2 && !data.children)}
    >
      <NavItem
        ref={navItemRef}
        // slots
        path={data.path}
        icon={data.icon}
        info={data.info}
        title={data.title}
        caption={data.caption}
        // state
        open={open}
        active={isActive}
        disabled={data.disabled}
        // options
        depth={depth}
        render={render}
        hasChild={!!data.children}
        externalLink={isExternalLink(data.path)}
        enabledRootRedirect={enabledRootRedirect}
        // styles
        slotProps={depth === 1 ? slotProps?.rootItem : slotProps?.subItem}
        // actions
        onClick={handleToggleMenu}
      />
    </NavItemWithFavorite>
  );

  const renderCollapse = () =>
    !!data.children && (
      <NavCollapse mountOnEnter unmountOnExit depth={depth} in={open} data-group={data.title}>
        <NavSubList
          data={data.children}
          render={render}
          depth={depth}
          slotProps={slotProps}
          checkPermissions={checkPermissions}
          enabledRootRedirect={enabledRootRedirect}
          parentPath={data.path}
        />
      </NavCollapse>
    );

  // Hidden item by role
  if (data.allowedRoles && checkPermissions && checkPermissions(data.allowedRoles)) {
    return null;
  }

  return (
    <NavLi
      disabled={data.disabled}
      sx={{
        ...(!!data.children && {
          [`& .${navSectionClasses.li}`]: { '&:first-of-type': { mt: 'var(--nav-item-gap)' } },
        }),
      }}
    >
      {renderNavItem()}
      {renderCollapse()}
    </NavLi>
  );
}

// ----------------------------------------------------------------------

function NavSubList({
  data,
  render,
  depth = 0,
  slotProps,
  checkPermissions,
  enabledRootRedirect,
  parentPath,
}: NavSubListProps & { parentPath?: string }) {
  return (
    <NavUl sx={{ gap: 'var(--nav-item-gap)' }}>
      {data.map((list) => (
        <NavList
          key={list.title}
          data={list}
          render={render}
          depth={depth + 1}
          slotProps={slotProps}
          checkPermissions={checkPermissions}
          enabledRootRedirect={enabledRootRedirect}
          parentPath={parentPath}
        />
      ))}
    </NavUl>
  );
}
